import { useCallback, useState } from "react";
import axios from "axios";

import { TenantInfo } from "../types/api/tenantTableTypes";
import { useMessage } from "./useMessage";
import { useLoginUser } from "../hooks/useLoginUser";
import appconfig from "../config.json";

// テナントパラメータの型定義
type TenantParameter = {
  parampath: string; // 必須項目
  name?: string;
  value?: string;
  lambdaArn?: string;
  apiName?: string;
  [key: string]: any; // その他の任意プロパティ
};

export const useTenant = () => {
  const { showMessage } = useMessage();
  const { loginUser, tokenRefresh } = useLoginUser();  const [loading, setLoading] = useState(false);
  const [tenant, setTenant] = useState<TenantInfo>();
  const [tenantparam, setTenantparam] = useState<TenantParameter[]>();

  const TENANT_MANAGER_URL: string = appconfig["IOT_MANAGEMENT_API_URL"];

  type IPostResponse = {
    access?: string;
    token?: string;
    newPasswordRequired?: string;
  };

  const tenantEdit = useCallback(
    (tenant: TenantInfo) => {
      if (tokenRefresh) {
        setLoading(true);
        new Promise((resolve) => resolve(tokenRefresh())).then((token) => {
          axios
            .put<any>(TENANT_MANAGER_URL + "/tenant", tenant, {
              headers: { Authorization: `Bearer ${token}` },
            })
            .then(async (res) => {
              if (res.data) {
                console.log(res.data);
                showMessage({ title: "更新しました。", status: "success" });
                setLoading(false);
              } else {
                showMessage({ title: res.statusText, status: "error" });
                setLoading(false);
              }
            })
            .catch(() => {
              showMessage({
                title: "テナントを更新できません",
                status: "error",
              });
              setLoading(false);
            });
        });
      }
    },
    [tokenRefresh]
  );
  const tenantStorageUpdate = useCallback(
    (tenant: TenantInfo) => {
      if (tokenRefresh) {
        setLoading(true);
        new Promise((resolve) => resolve(tokenRefresh())).then((token) => {
          axios
            .put<any>(
              TENANT_MANAGER_URL + "/tenantstorage/" + tenant.tenant_id,
              tenant,
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            )
            .then(async (res) => {
              if (res.data) {
                console.log(res.data);
                showMessage({ title: "更新しました。", status: "success" });
                setLoading(false);
              } else {
                showMessage({ title: res.statusText, status: "error" });
                setLoading(false);
              }
            })
            .catch(() => {
              showMessage({
                title: "テナントを更新できません",
                status: "error",
              });
              setLoading(false);
            });
        });
      }
    },
    [tokenRefresh]
  );
  const tenantDelete = useCallback(
    (tenant_id: string) => {
      if (tokenRefresh) {
        setLoading(true);
        new Promise((resolve) => resolve(tokenRefresh())).then((token) => {
          axios
            .delete<any>(TENANT_MANAGER_URL + "/tenant/" + tenant_id, {
              headers: { Authorization: `Bearer ${token}` },
            })
            .then(async (res) => {
              if (res.status == 204) {
                showMessage({ title: "削除しました。", status: "success" });
                setLoading(false);
              } else if (res.status == 403) {
                showMessage({
                  title: "権限エラー:" + res.data,
                  status: "error",
                });
                setLoading(false);
              } else {
                showMessage({ title: res.statusText, status: "error" });
                setLoading(false);
              }
            })
            .catch(() => {
              showMessage({
                title: "テナントが削除できません",
                status: "error",
              });
              setLoading(false);
            });
        });
      }
    },
    [tokenRefresh]
  );

  const tenantGet = useCallback(
    (tenant_id: string) => {
      if (tokenRefresh) {
        setLoading(true);
        new Promise((resolve) => resolve(tokenRefresh())).then((token) => {
          axios
            .get<TenantInfo>(TENANT_MANAGER_URL + "/tenant/" + tenant_id, {
              headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => setTenant(res.data))
            .catch(() =>
              showMessage({
                title: "テナント取得に失敗しました",
                status: "error",
              })
            )
            .finally(() => setLoading(false));
        });
      }
    },
    [tokenRefresh]
  );

  // テナントリソース更新APIを呼び出す関数
  const tenantResourcesUpdate = useCallback(
    (tenant_id: string) => {
      if (tokenRefresh) {
        setLoading(true);
        new Promise((resolve) => resolve(tokenRefresh())).then((token) => {
          axios
            .put<TenantInfo>(
              TENANT_MANAGER_URL + "/tenantresources/" + tenant_id,
              {},
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            )
            .then(async (res) => {
              if (res.data) {
                console.log(res.data);
                showMessage({
                  title: "リソースを更新しました。",
                  status: "success",
                });
                setLoading(false);
              } else {
                showMessage({ title: res.statusText, status: "error" });
                setLoading(false);
              }
            })
            .catch((error) => {
              showMessage({
                title:
                  "テナントリソースを更新できません: " +
                  (error.response?.data || error.message),
                status: "error",
              });
              setLoading(false);
            });
        });
      }
    },
    [tokenRefresh]
  );
  const getTenantParam = useCallback(
    (tenant_id: string) => {
      setLoading(true);
      const path = `${tenant_id}`;
      new Promise((resolve) => resolve(tokenRefresh())).then((token) => {        axios
          .get<TenantParameter[]>(TENANT_MANAGER_URL + `/parameters?path=${path}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          .then((res) => {
            setTenantparam(res.data);
            setLoading(false);
          })
          .catch(() => {
            showMessage({
              title: "テナントパラメータの取得に失敗しました",
              status: "error",
            });
            setTenantparam(undefined);
            setLoading(false);
          });
      });
    },
    [tokenRefresh]
  );

  // テナントパラメータから特定のパスの値を取得するヘルパー関数
  const getTenantParamByPath = useCallback((paramPath: string) => {
    if (!tenantparam) return undefined;
    
    const param = tenantparam.find(p => p.parampath === paramPath);
    return param;
  }, [tenantparam]);

  // AuthorizerFunction-arnを取得するヘルパー関数
  const getAuthorizerFunctionArn = useCallback((tenantId: string) => {
    const param = getTenantParamByPath(`${tenantId}/AuthorizerFunction-arn`);
    return param?.value;
  }, [getTenantParamByPath]);

  const tenantStorageDelete = useCallback(
    (tenant_id: string) => {
      if (tokenRefresh) {
        setLoading(true);
        new Promise((resolve) => resolve(tokenRefresh())).then((token) => {
          axios
            .delete<any>(TENANT_MANAGER_URL + "/tenantstorage/" + tenant_id, {
              headers: { Authorization: `Bearer ${token}` },
            })
            .then(async (res) => {
              if (res.data) {
                console.log(res.data);
                showMessage({ title: "ストレージを削除しました。", status: "success" });
                setLoading(false);
              } else {
                showMessage({ title: res.statusText, status: "error" });
                setLoading(false);
              }
            })
            .catch((error) => {
              showMessage({
                title: "テナントストレージを削除できません: " + (error.response?.data?.message || error.message),
                status: "error",
              });
              setLoading(false);
            });
        });
      }
    },
    [tokenRefresh]
  );

  const tenantResourcesDelete = useCallback(
    (tenant_id: string) => {
      if (tokenRefresh) {
        setLoading(true);
        new Promise((resolve) => resolve(tokenRefresh())).then((token) => {
          axios
            .delete<any>(TENANT_MANAGER_URL + "/tenantresources/" + tenant_id, {
              headers: { Authorization: `Bearer ${token}` },
            })
            .then(async (res) => {
              if (res.data) {
                console.log(res.data);
                showMessage({
                  title: "リソースを削除しました。",
                  status: "success",
                });
                setLoading(false);
              } else {
                showMessage({ title: res.statusText, status: "error" });
                setLoading(false);
              }
            })
            .catch((error) => {
              showMessage({
                title:
                  "テナントリソースを削除できません: " +
                  (error.response?.data?.message || error.message),
                status: "error",
              });
              setLoading(false);
            });
        });
      }
    },
    [tokenRefresh]
  );

  return {
    tenantGet,
    tenantEdit,
    tenantDelete,
    tenantStorageUpdate,
    tenantResourcesUpdate,
    loading,
    tenant,
    getTenantParam,
    tenantparam,
    getTenantParamByPath,
    getAuthorizerFunctionArn,
    tenantStorageDelete,
    tenantResourcesDelete,
  };
};
